import React, { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import StyledModal from "components/Modal";
import WarningBox from "components/WarningBox";
import { FormNumericInput } from "components/FormInput";
import StyledFormRadioButton from "components/FormInput/FormRadioButton";

import { useAppDispatch } from "store/hooks";
import { setNotification } from "store/features/notificationSlice";
import { updateInvoiceExpirationSettings } from "store/features/invoiceExpirationSettingsSlice";

import {
  FormValues,
  IbanInvoiceValidity,
  options,
  validationSchema,
} from "./validation";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

interface UpdateExpirationModalProps {
  defaultValues: FormValues;
  isOpen: boolean;
  onClose: () => void;
}

const UpdateExpirationModal: React.FC<UpdateExpirationModalProps> = (props) => {
  const { isOpen, defaultValues, onClose } = props;
  const dispatch = useAppDispatch();

  const methods = useForm<FormValues>({
    mode: "onSubmit",
    resolver: yupResolver(validationSchema),
  });

  const {
    formState: { errors },
  } = methods;

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues]);

  const validity = methods.watch("validity");

  const onSubmit = async () => {
    analytics().sendEvent(AnalyticsEvents.settings_update_exp_modal_confirm);
    await methods.handleSubmit(async (values: FormValues) => {
      const hours = Number(values.hours || 0);
      const days = Number(values.days || 0);

      if (validity === IbanInvoiceValidity.Limited) {
        await dispatch(
          updateInvoiceExpirationSettings({ hours, days })
        ).unwrap();
      } else {
        await dispatch(updateInvoiceExpirationSettings(undefined)).unwrap();
      }

      onCloseModal();

      dispatch(
        setNotification({
          severity: "success",
          message: "Налаштування успішно оновлено",
        })
      );
      analytics().sendEvent(AnalyticsEvents.settings_invoice_exp_updated);
    })();
  };

  const onCloseModal = () => {
    onClose();
  };

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onCloseModal}
      title={"Термін дії посилання"}
      content={
        <>
          <WarningBox content="Зверніть увагу! Зміна терміну дії вплине лише на майбутні посилання і не змінить строк дії вже створених посилань." />
          <FormProvider {...methods}>
            <StyledFormRadioButton
              options={options}
              name="validity"
              sx={{ mb: 0 }}
            />
            {validity === IbanInvoiceValidity.Limited && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 0, sm: 1.5 },
                  }}
                >
                  <FormNumericInput
                    title="Дні"
                    name="days"
                    placeholder="Введіть кількість днів"
                    sx={{ minWidth: { xs: "100%", sm: "218px" } }}
                    maxValue={1000}
                    decimalScale={0}
                  />
                  <FormNumericInput
                    title="Години"
                    name="hours"
                    placeholder="Введіть кількість годин"
                    sx={{ minWidth: { xs: "100%", sm: "218px" } }}
                    maxValue={1000}
                    decimalScale={0}
                  />
                </Box>
                {errors.form && (
                  <Typography variant="subtitle2" color="error.dark">
                    {errors.form.message}
                  </Typography>
                )}
              </>
            )}
          </FormProvider>
        </>
      }
      onAction={onSubmit}
    />
  );
};

export default UpdateExpirationModal;
